import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly keyPrefix = `saferwise-driver:${environment.name}`;
  private readonly loginKey = `${this.keyPrefix}:isLoggedIn`;
  private readonly accessTokenKey = `${this.keyPrefix}:accessToken`;
  private readonly rememberMeTokenKey = `${this.keyPrefix}:rememberMeToken`;
  private readonly userDataKey = `${this.keyPrefix}:userData`;

  private loggedInStatus = this.readBoolean(this.loginKey, false);

  setLoginStatus(value: boolean): void {
    this.loggedInStatus = value;
    this.storageSetItem(this.loginKey, String(value));
  }

  get LoginStatus(): boolean {
    return this.readBoolean(this.loginKey, this.loggedInStatus);
  }

  setToken(token: string): void {
    this.storageSetItem(this.accessTokenKey, token);
  }

  rememberMeSetToken(token: string): void {
    this.storageSetItem(this.rememberMeTokenKey, token);
  }

  getToken(): string | null {
    return this.storageGetItem(this.accessTokenKey);
  }

  rememberMeToken(): string | null {
    return this.storageGetItem(this.rememberMeTokenKey);
  }

  setUserInfo(data: unknown): void {
    this.storageSetItem(this.userDataKey, JSON.stringify(data));
  }

  getUserInfo<T = unknown>(): T | null {
    const raw = this.storageGetItem(this.userDataKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  logout(): void {
    this.storageRemoveItem(this.accessTokenKey);
    this.storageRemoveItem(this.rememberMeTokenKey);
    this.storageSetItem(this.loginKey, 'false');
    this.storageRemoveItem(this.userDataKey);
  }

  private storageGetItem(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private storageSetItem(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Safari can block storage in some privacy modes.
    }
  }

  private storageRemoveItem(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // No-op when storage is unavailable.
    }
  }

  private readBoolean(key: string, fallback: boolean): boolean {
    const raw = this.storageGetItem(key);
    if (raw == null) return fallback;
    try {
      return JSON.parse(raw) as boolean;
    } catch {
      return fallback;
    }
  }
}
